import {
  TFormattedWorkflowDefinitionValidationError,
  TRedirectStepDefinition,
  TWorkflowDefinition,
  WorkflowDefinitionValidationRule,
  WorkflowDefinitionValidationSeverityType,
  WorkflowStepType,
  WorkflowValidationErrorCode,
} from '@commenda/types';

const TEMPLATE_VAR_RE = /\{\{([^}]+)\}\}/g;
const COMMENDA_ROOT_DOMAIN = 'commenda.io';

export function extractUrlVariables(url: string): string[] {
  const matches = Array.from(url.matchAll(TEMPLATE_VAR_RE));
  return Array.from(new Set(matches.map((m) => m[1].trim())));
}

export function isValidRedirectUrl(rawUrl: string): boolean {
  const url = rawUrl.replace(TEMPLATE_VAR_RE, 'PLACEHOLDER');
  if (/^\/(?!\/)/.test(url)) return true;
  if (!/^https?:\/\//i.test(url)) return false;
  try {
    const { hostname } = new URL(url);
    return !hostname.endsWith(`.${COMMENDA_ROOT_DOMAIN}`);
  } catch {
    return false;
  }
}

export function validateRedirectNodes(
  definition: TWorkflowDefinition
): TFormattedWorkflowDefinitionValidationError[] {
  const errors: TFormattedWorkflowDefinitionValidationError[] = [];

  const redirectSteps = definition.steps.filter(
    (s): s is TRedirectStepDefinition =>
      s.type === WorkflowStepType.HUMAN_REDIRECT
  );

  redirectSteps.forEach((step) => {
    if (!step.redirectUrl) {
      errors.push({
        code: WorkflowValidationErrorCode.REDIRECT_CONFIG_INVALID,
        rule: WorkflowDefinitionValidationRule.REDIRECT_STEP_URL_MISSING,
        message: `Redirect step "${step.name}" is missing a redirect URL.`,
        severity: WorkflowDefinitionValidationSeverityType.ERROR,
        details: { stepId: step.id },
      });
      return;
    }

    if (!isValidRedirectUrl(step.redirectUrl)) {
      errors.push({
        code: WorkflowValidationErrorCode.REDIRECT_CONFIG_INVALID,
        rule: WorkflowDefinitionValidationRule.REDIRECT_STEP_URL_INVALID,
        message: `Redirect step "${step.name}" has an invalid URL. Use a relative path (e.g. /path) or a full URL starting with https://.`,
        severity: WorkflowDefinitionValidationSeverityType.ERROR,
        details: { stepId: step.id, redirectUrl: step.redirectUrl },
      });
      return;
    }

    const variables = extractUrlVariables(step.redirectUrl);
    const valueMapping = step.valueMapping ?? {};
    const missingBindings = variables.filter((v) => !valueMapping[v]);

    if (missingBindings.length > 0) {
      errors.push({
        code: WorkflowValidationErrorCode.REDIRECT_CONFIG_INVALID,
        rule: WorkflowDefinitionValidationRule.REDIRECT_STEP_MISSING_BINDING,
        message: `Redirect step "${step.name}" has unbound URL variables: ${missingBindings.join(', ')}.`,
        severity: WorkflowDefinitionValidationSeverityType.ERROR,
        details: { stepId: step.id, missingBindings },
      });
    }
  });

  return errors;
}
